import React, { useState, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import { Card } from "components/ui/card";
import { Button } from "components/ui/button";
import PageWrapper from "components/PageWrapper";
import CompanyUsersList from "components/settings/CompanyUsersList";
import AddUserModal from "components/settings/AddUserModal";
import { getAllCompanyUsersApi } from "actions/userAction";
import { getUserRole } from "utils/helper";

// Tile accents mirror the SmartBuy prototype `.stat-tile` solid top border.
const TILE_TONES = {
  violet: "border-t-s360-violet",
  teal: "border-t-s360-teal",
  amber: "border-t-s360-warning",
  blue: "border-t-s360-info",
};

const StatTile = ({ tone, label, value, foot }) => (
  <div className={`bg-white rounded-md shadow-sm p-4 flex flex-col gap-1 border-t-[3px] ${TILE_TONES[tone]}`}>
    <span className="text-xs font-semibold uppercase tracking-[0.08em] text-s360-fg3">
      {label}
    </span>
    <span className="text-[22px] leading-[28px] font-bold tracking-[-0.015em] text-s360-ink tabular-nums">
      {value}
    </span>
    <span className="text-xs text-s360-fg3">{foot}</span>
  </div>
);

const roleOf = (u) => getUserRole(u);
const isAdminRole = (r) => ["admin", "super_admin", "owner"].includes(r);

const UserManagementPage = () => {
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersRefreshKey, setUsersRefreshKey] = useState(0);

  const fetchUsers = useCallback(async () => {
    const response = await getAllCompanyUsersApi();
    if (response?.status) {
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : data?.results || data?.data || data?.users || data?.rows || [];
      setUsers(Array.isArray(list) ? list : []);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const total = users.length;
  const admins = users.filter((u) => isAdminRole(roleOf(u))).length;
  const orderAdmins = users.filter((u) => roleOf(u) === "order_admin").length;
  const standard = users.filter((u) => {
    const r = roleOf(u);
    return !isAdminRole(r) && r !== "order_admin";
  }).length;

  return (
    <PageWrapper>
      <div className="flex flex-col gap-6">
        <div className="flex items-end justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
              User management
            </h1>
            <span className="text-sm text-s360-fg3">
              Invite teammates and control what they can access.
            </span>
          </div>
          <Button
            size="sm"
            variant="primary"
            className="min-w-[88px] h-8 rounded-md bg-s360-violet text-white shadow-sm hover:bg-s360-violet-600 transition-colors disabled:opacity-60"
            onClick={() => setIsAddUserModalOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add user
          </Button>
        </div>

        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile tone="violet" label="Team members" value={total} foot="across all roles" />
          <StatTile tone="teal" label="Admins" value={admins} foot="full access" />
          <StatTile tone="amber" label="Order admins" value={orderAdmins} foot="can place orders" />
          <StatTile tone="blue" label="Standard users" value={standard} foot="view-only" />
        </section>

        <section>
          <Card className="p-0 relative overflow-hidden">
            <div className="flex items-center justify-between gap-3 p-6 pb-4 border-b border-s360-stroke1">
              <h2 className="text-base font-semibold text-s360-fg1">Members</h2>
              <span className="text-xs text-s360-fg3">{total} team members</span>
            </div>
            <CompanyUsersList refreshKey={usersRefreshKey} />
          </Card>
        </section>
      </div>

      <AddUserModal
        isAddUserModalOpen={isAddUserModalOpen}
        setIsAddUserModalOpen={setIsAddUserModalOpen}
        onSuccess={() => {
          fetchUsers();
          setUsersRefreshKey((k) => k + 1);
        }}
      />
    </PageWrapper>
  );
};

export default UserManagementPage;
